import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Search, User, Users, GraduationCap, Briefcase, ShoppingCart, Shield, X, Loader2 } from 'lucide-react';

const ENTITY_CONFIG = {
  player: {
    icon: Users,
    label: 'Player',
    color: 'bg-blue-100 text-blue-700',
    path: '/admin/players'
  },
  parent: {
    icon: User,
    label: 'Parent',
    color: 'bg-green-100 text-green-700',
    path: '/admin/parents'
  },
  customer: {
    icon: ShoppingCart,
    label: 'Customer',
    color: 'bg-orange-100 text-orange-700',
    path: '/admin/customers'
  },
  coach: {
    icon: GraduationCap,
    label: 'Coach',
    color: 'bg-purple-100 text-purple-700',
    path: '/admin/coaches'
  },
  staff: {
    icon: Briefcase,
    label: 'Staff',
    color: 'bg-amber-100 text-amber-700',
    path: '/admin/staff'
  },
  admin: {
    icon: Shield,
    label: 'Admin',
    color: 'bg-rose-100 text-rose-700',
    path: '/admin/staff'
  }
};

const GlobalSearch = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      if (event.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const searchEntities = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/admin/global-search?q=${encodeURIComponent(searchQuery)}`);
      setResults(response.data.results || []);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchEntities(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, searchEntities]);

  const handleResultClick = (result) => {
    const config = ENTITY_CONFIG[result.type];
    if (config) {
      navigate(`${config.path}/${result.id}`);
    }
    setIsOpen(false);
    setQuery('');
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    inputRef.current?.focus();
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={isStaff ? "Search players, parents, customers... (Ctrl+K)" : "Search players, parents, customers, coaches, staff... (Ctrl+K)"}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          className="pl-10 pr-10 bg-white border-slate-200"
          data-testid="global-search-input"
        />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && (query.length >= 2 || results.length > 0) && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-slate-200 max-h-96 overflow-y-auto z-50">
          {loading ? (
            <div className="p-4 text-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              <span className="text-sm">Searching...</span>
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-slate-500 text-sm">
              {query.length < 2 ? 'Type at least 2 characters to search' : 'No results found'}
            </div>
          ) : (
            <div className="py-2">
              {results.map((result, index) => {
                const config = ENTITY_CONFIG[result.type];
                if (!config) return null;
                const Icon = config.icon;

                return (
                  <button
                    key={`${result.type}-${result.id}-${index}`}
                    onClick={() => handleResultClick(result)}
                    className="w-full px-4 py-3 hover:bg-slate-50 flex items-center gap-3 text-left transition-colors"
                    data-testid={`search-result-${result.type}-${result.id}`}
                  >
                    <div className={`p-2 rounded-full ${config.color}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 truncate">
                          {result.name}
                        </span>
                        {result.numeric_id && (
                          <span className="text-xs text-slate-400 font-mono">
                            {result.numeric_id}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className={`text-xs ${config.color}`}>
                          {config.label}
                        </Badge>
                        {result.email && (
                          <span className="text-xs text-slate-500 truncate">
                            {result.email}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
